import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Upload, Trash2, Loader2, FileText, Download, Inbox, Eye, Users, RefreshCw, Pencil, Quote } from "lucide-react";
import { API, api } from "@/lib/api";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function AdminDashboard() {
  const location = useLocation();
  const [auth, setAuth] = useState(location.state?.user || null);
  const [checked, setChecked] = useState(!!location.state?.user);
  const [newsletters, setNewsletters] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [listingsMeta, setListingsMeta] = useState(null);
  const [refreshingListings, setRefreshingListings] = useState(false);
  const [tab, setTab] = useState("newsletters");
  const [title, setTitle] = useState("Lagoon City Market Report");
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reviewForm, setReviewForm] = useState({ quote: "", author: "", context: "" });
  const [editingReview, setEditingReview] = useState(null);
  const [savingReview, setSavingReview] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [events, setEvents] = useState([]);
  const emptyBiz = { name: "", kind: "", blurb: "", phone: "", url: "", url_label: "", img: "", logo: false };
  const emptyEvent = { title: "", date: "", location: "", description: "", img: "", url: "", start: "", end: "", all_day: false, rrule: "" };
  const [bizForm, setBizForm] = useState(emptyBiz);
  const [editingBiz, setEditingBiz] = useState(null);
  const [eventForm, setEventForm] = useState(emptyEvent);
  const [editingEvent, setEditingEvent] = useState(null);
  const fileRef = useRef(null);
  const reviewFormRef = useRef(null);
  const bizFormRef = useRef(null);
  const eventFormRef = useRef(null);

  useEffect(() => {
    if (location.state?.user) return;
    api
      .get("/auth/me")
      .then((res) => setAuth(res.data))
      .catch(() => setAuth(null))
      .finally(() => setChecked(true));
  }, [location.state]);

  const loadData = () => {
    api.get("/newsletters").then((res) => setNewsletters(res.data)).catch(() => {});
    api.get("/contact").then((res) => setContacts(res.data)).catch(() => {});
    api.get("/subscribers").then((res) => setSubscribers(res.data)).catch(() => {});
    api.get("/listings/meta").then((res) => setListingsMeta(res.data)).catch(() => {});
    api.get("/testimonials").then((res) => setReviews(res.data)).catch(() => {});
    api.get("/businesses").then((res) => setBusinesses(res.data)).catch(() => {});
    api.get("/events").then((res) => setEvents(res.data)).catch(() => {});
  };

  useEffect(() => {
    if (auth?.is_admin) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setAuth(null);
  };

  const refreshListings = async () => {
    setRefreshingListings(true);
    try {
      const res = await api.post("/listings/refresh");
      toast.success(`Listings synced — ${res.data.count} live listings pulled from the Lagoon City search.`);
      loadData();
    } catch {
      toast.error("Listing sync failed — the daily auto-sync will retry.");
    } finally {
      setRefreshingListings(false);
    }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose a PDF file first.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("month", String(month));
      fd.append("year", String(year));
      fd.append("file", file);
      const res = await api.post("/newsletters", fd);
      const emailed = res.data?.emailed || 0;
      toast.success(
        `${MONTHS[month - 1]} ${year} published` +
          (emailed ? ` — emailed to ${emailed} subscriber${emailed === 1 ? "" : "s"}.` : ".")
      );
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (n) => {
    try {
      await api.delete(`/newsletters/${n.newsletter_id}`);
      toast.success(`${n.month_name} ${n.year} removed.`);
      loadData();
    } catch {
      toast.error("Could not delete that issue.");
    }
  };

  const removeSubscriber = async (s) => {
    try {
      await api.delete(`/subscribers/${s.subscriber_id}`);
      toast.success(`${s.email} removed from the list.`);
      loadData();
    } catch {
      toast.error("Could not remove that subscriber.");
    }
  };

  const saveReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.quote.trim() || !reviewForm.author.trim()) {
      toast.error("A review needs at least a quote and an author.");
      return;
    }
    setSavingReview(true);
    try {
      if (editingReview) {
        await api.put(`/testimonials/${editingReview}`, reviewForm);
        toast.success("Review updated.");
      } else {
        await api.post("/testimonials", reviewForm);
        toast.success("Review added to the site.");
      }
      setReviewForm({ quote: "", author: "", context: "" });
      setEditingReview(null);
      loadData();
    } catch {
      toast.error("Could not save the review.");
    } finally {
      setSavingReview(false);
    }
  };

  const startEditReview = (r) => {
    setEditingReview(r.testimonial_id);
    setReviewForm({ quote: r.quote, author: r.author, context: r.context || "" });
    reviewFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const cancelEditReview = () => {
    setEditingReview(null);
    setReviewForm({ quote: "", author: "", context: "" });
  };

  const removeReview = async (r) => {
    try {
      await api.delete(`/testimonials/${r.testimonial_id}`);
      toast.success(`Review from ${r.author} removed.`);
      if (editingReview === r.testimonial_id) cancelEditReview();
      loadData();
    } catch {
      toast.error("Could not remove that review.");
    }  };

  const saveBiz = async (e) => {
    e.preventDefault();
    if (!bizForm.name.trim()) { toast.error("A business needs a name."); return; }
    try {
      if (editingBiz) { await api.put(`/businesses/${editingBiz}`, bizForm); toast.success("Business updated."); }
      else { await api.post("/businesses", bizForm); toast.success("Business added."); }
      setBizForm(emptyBiz); setEditingBiz(null); loadData();
    } catch { toast.error("Could not save the business."); }
  };
  const startEditBiz = (b) => {
    setEditingBiz(b.business_id);
    setBizForm({ name: b.name, kind: b.kind || "", blurb: b.blurb || "", phone: b.phone || "", url: b.url || "", url_label: b.url_label || "", img: b.img || "", logo: !!b.logo });
    bizFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const removeBiz = async (b) => {
    try { await api.delete(`/businesses/${b.business_id}`); toast.success(`${b.name} removed.`); if (editingBiz === b.business_id) { setEditingBiz(null); setBizForm(emptyBiz); } loadData(); }
    catch { toast.error("Could not remove that business."); }
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title.trim()) { toast.error("An event needs a title."); return; }
    try {
      if (editingEvent) { await api.put(`/events/${editingEvent}`, eventForm); toast.success("Event updated."); }
      else { await api.post("/events", eventForm); toast.success("Event added."); }
      setEventForm(emptyEvent); setEditingEvent(null); loadData();
    } catch { toast.error("Could not save the event."); }
  };
  const startEditEvent = (ev) => {
    setEditingEvent(ev.event_id);
    setEventForm({ title: ev.title, date: ev.date || "", location: ev.location || "", description: ev.description || "", img: ev.img || "", url: ev.url || "", start: ev.start || "", end: ev.end || "", all_day: ev.all_day || false, rrule: ev.rrule || "" });
    eventFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const removeEvent = async (ev) => {
    try { await api.delete(`/events/${ev.event_id}`); toast.success(`${ev.title} removed.`); if (editingEvent === ev.event_id) { setEditingEvent(null); setEventForm(emptyEvent); } loadData(); }
    catch { toast.error("Could not remove that event."); }
  };

  const inputCls =
    "w-full rounded-xl bg-[#0D0B0E]/70 border border-[#E890A7]/20 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E890A7]/70 transition-colors";

  if (!checked) {
    return (
      <div data-testid="admin-loading" className="min-h-screen bg-[#0D0B0E] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#E890A7]/30 border-t-[#E890A7] rounded-full animate-spin" />
      </div>
    );
  }

  if (!auth) {
    return (
      <div data-testid="admin-signin-screen" className="min-h-screen bg-[#0D0B0E] text-slate-100 flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <span className="inline-block bg-white/95 rounded-full px-5 py-2.5 mb-8">
            <img src="/assets/joselle-logo.png" alt="Joselle Alves Broker" className="h-9 w-auto" />
          </span>
          <h1 className="font-display text-4xl font-light">Owner Portal</h1>
          <p className="text-slate-400 text-sm mt-4 mb-8 font-light">
            Sign in with Google to upload market reports and review enquiries.
          </p>
          <button
            data-testid="admin-signin-btn"
            onClick={signIn}
            className="w-full rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 hover:bg-[#F2A7BA] transition-colors"
          >
            Sign in with Google
          </button>
          <Link data-testid="admin-back-home" to="/" className="block mt-6 text-xs text-slate-500 hover:text-[#F2A7BA] transition-colors">
            ← Back to the site
          </Link>
        </div>
      </div>
    );
  }

  if (!auth.is_admin) {
    return (
      <div data-testid="admin-no-access" className="min-h-screen bg-[#0D0B0E] text-slate-100 flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <h1 className="font-display text-4xl font-light">No admin access</h1>
          <p className="text-slate-400 text-sm mt-4 mb-8 font-light">
            {auth.email} isn&apos;t on the broker admin list. Ask the site administrator to add it.
          </p>
          <button
            data-testid="admin-signout-btn"
            onClick={logout}
            className="rounded-full border border-[#E890A7]/40 text-[#F2A7BA] px-7 py-3 hover:bg-[#E890A7]/10 transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0D0B0E] text-slate-100">
      <header className="border-b border-[#E890A7]/10 bg-[#0D0B0E]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="bg-white/95 rounded-full px-3 py-1.5">
              <img src="/assets/joselle-logo.png" alt="Joselle Alves Broker" className="h-6 w-auto" />
            </span>
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E890A7]/80">Owner Portal</span>
          </div>
          <div className="flex items-center gap-5">
            <button
              data-testid="admin-refresh-listings-btn"
              onClick={refreshListings}
              disabled={refreshingListings}
              className="flex items-center gap-2 text-xs text-slate-400 hover:text-[#F2A7BA] transition-colors disabled:opacity-50"
            >
              {refreshingListings ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Sync listings
            </button>
            <Link data-testid="admin-view-site" to="/" className="text-xs text-slate-400 hover:text-[#F2A7BA] transition-colors">
              View site
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 text-xs text-slate-400 hover:text-[#F2A7BA] transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="font-display font-light text-4xl sm:text-5xl">
          Hello, <em className="italic text-[#E890A7]">{auth.name?.split(" ")[0] || "Joselle"}</em>
        </h1>
        <p className="text-slate-500 text-sm mt-2">{auth.email}</p>
        <p data-testid="admin-listings-meta" className="text-slate-600 text-xs mt-1">
          {listingsMeta?.refreshed_at
            ? `Listings last synced ${new Date(listingsMeta.refreshed_at).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" })} · ${listingsMeta.count} shown · auto-syncs daily`
            : "Listings sync daily from the live Lagoon City search"}
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-10">
          {[
            { icon: FileText, label: "Market Reports", value: newsletters.length },
            { icon: Download, label: "Total downloads", value: newsletters.reduce((a, n) => a + n.downloads, 0) },
            { icon: Users, label: "Subscribers", value: subscribers.length },
            { icon: Inbox, label: "Enquiries", value: contacts.length },
            { icon: Quote, label: "Reviews", value: reviews.length },
          ].map((s) => (
            <div key={s.label} data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-2xl border border-[#E890A7]/15 bg-[#151218]/70 p-5">
              <s.icon size={18} className="text-[#E890A7] mb-3" />
              <p className="font-display text-3xl">{s.value}</p>
              <p className="text-[11px] text-slate-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-12 mb-8" data-testid="admin-tabs">
          {[
            { id: "newsletters", label: "Market Reports" },
            { id: "reviews", label: "Reviews" },
            { id: "businesses", label: "Businesses" },
            { id: "events", label: "Events" },
            { id: "enquiries", label: "Enquiries" },
            { id: "subscribers", label: "Subscribers" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-5 py-2.5 text-sm transition-all duration-300 ${
                tab === t.id
                  ? "bg-[#E890A7] text-[#0D0B0E] font-semibold"
                  : "border border-[#E890A7]/25 text-slate-400 hover:text-[#F2A7BA]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "newsletters" && (
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8">
            <form
              data-testid="newsletter-upload-form"
              onSubmit={upload}
              className="rounded-3xl border border-[#E890A7]/20 bg-[#151218]/80 p-7 h-fit"
            >
              <h2 className="font-display text-2xl mb-2">Upload a new edition</h2>
              <p className="text-xs text-slate-500 mb-6">
                Publishing emails the new edition to all {subscribers.length} subscriber{subscribers.length === 1 ? "" : "s"} automatically.
              </p>
              <label className="block text-xs font-mono tracking-widest uppercase text-slate-500 mb-2">Title</label>
              <input
                data-testid="newsletter-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className={inputCls}
              />
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-slate-500 mb-2">Month</label>
                  <select
                    data-testid="newsletter-month-select"
                    value={month}
                    onChange={(e) => setMonth(Number(e.target.value))}
                    className={`${inputCls} appearance-none`}
                  >
                    {MONTHS.map((m, i) => (
                      <option key={m} value={i + 1} className="bg-[#151218]">
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-slate-500 mb-2">Year</label>
                  <input
                    data-testid="newsletter-year-input"
                    type="number"
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    required
                    className={inputCls}
                  />
                </div>
              </div>
              <label className="block text-xs font-mono tracking-widest uppercase text-slate-500 mt-4 mb-2">
                PDF file
              </label>
              <input
                data-testid="newsletter-file-input"
                ref={fileRef}
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-slate-400 file:mr-4 file:rounded-full file:border-0 file:bg-[#E890A7]/15 file:text-[#F2A7BA] file:px-5 file:py-2.5 file:text-sm hover:file:bg-[#E890A7]/25 file:transition-colors"
              />
              <button
                data-testid="newsletter-upload-btn"
                type="submit"
                disabled={uploading}
                className="mt-6 w-full rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 flex items-center justify-center gap-2 hover:bg-[#F2A7BA] disabled:opacity-60 transition-colors"
              >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {uploading ? "Publishing…" : "Publish market report"}
              </button>
            </form>

            <div className="space-y-4" data-testid="admin-newsletter-list">
              {newsletters.length === 0 && (
                <p className="text-slate-500 text-sm">No market reports yet — upload the first one.</p>
              )}
              {newsletters.map((n) => (
                <div
                  key={n.newsletter_id}
                  data-testid={`admin-newsletter-row-${n.newsletter_id}`}
                  className="rounded-2xl border border-[#E890A7]/15 bg-[#151218]/70 p-5 flex items-center justify-between gap-4"
                >
                  <div>
                    <p className="font-display text-xl">
                      {n.month_name} {n.year}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {n.title} · {(n.file_size / (1024 * 1024)).toFixed(1)} MB · {n.downloads} downloads
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      data-testid={`admin-preview-${n.newsletter_id}`}
                      href={`${API}/newsletters/${n.newsletter_id}/file`}
                      target="_blank"
                      rel="noreferrer"
                      className="w-9 h-9 rounded-full border border-[#E890A7]/20 flex items-center justify-center text-slate-400 hover:text-[#F2A7BA] hover:border-[#E890A7]/50 transition-colors"
                      aria-label="Preview"
                    >
                      <Eye size={15} />
                    </a>
                    <button
                      data-testid={`admin-delete-${n.newsletter_id}`}
                      onClick={() => remove(n)}
                      className="w-9 h-9 rounded-full border border-red-400/20 flex items-center justify-center text-slate-400 hover:text-red-300 hover:border-red-400/50 transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8">
            <form
              data-testid="review-form"
              ref={reviewFormRef}
              onSubmit={saveReview}
              className="rounded-3xl border border-[#E890A7]/20 bg-[#151218]/80 p-7 h-fit"
            >
              <h2 className="font-display text-2xl mb-2">{editingReview ? "Edit review" : "Add a review"}</h2>
              <p className="text-xs text-slate-500 mb-6">
                Reviews appear in the &ldquo;Kind Words&rdquo; section of the site.
              </p>
              <label className="block text-xs font-mono tracking-widest uppercase text-slate-500 mb-2">Review</label>
              <textarea
                data-testid="review-quote-input"
                rows={6}
                value={reviewForm.quote}
                onChange={(e) => setReviewForm({ ...reviewForm, quote: e.target.value })}
                required
                placeholder="What the client said…"
                className={`${inputCls} resize-none`}
              />
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-slate-500 mb-2">Author</label>
                  <input
                    data-testid="review-author-input"
                    value={reviewForm.author}
                    onChange={(e) => setReviewForm({ ...reviewForm, author: e.target.value })}
                    required
                    placeholder="e.g. Janine Allen"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-slate-500 mb-2">Detail</label>
                  <input
                    data-testid="review-context-input"
                    value={reviewForm.context}
                    onChange={(e) => setReviewForm({ ...reviewForm, context: e.target.value })}
                    placeholder="e.g. First-time buyer"
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  data-testid="review-save-btn"
                  type="submit"
                  disabled={savingReview}
                  className="flex-1 rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 flex items-center justify-center gap-2 hover:bg-[#F2A7BA] disabled:opacity-60 transition-colors"
                >
                  {savingReview && <Loader2 size={16} className="animate-spin" />}
                  {editingReview ? "Save changes" : "Add review"}
                </button>
                {editingReview && (
                  <button
                    data-testid="review-cancel-edit-btn"
                    type="button"
                    onClick={cancelEditReview}
                    className="rounded-full border border-[#E890A7]/30 text-slate-400 px-6 py-3.5 hover:text-[#F2A7BA] transition-colors"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>

            <div className="space-y-4" data-testid="admin-reviews-list">
              {reviews.length === 0 && (
                <p className="text-slate-500 text-sm">No reviews yet — add the first one.</p>
              )}
              {reviews.map((r) => (
                <div
                  key={r.testimonial_id}
                  data-testid={`admin-review-row-${r.testimonial_id}`}
                  className={`rounded-2xl border p-5 ${
                    editingReview === r.testimonial_id
                      ? "border-[#E890A7]/60 bg-[#1E1A23]"
                      : "border-[#E890A7]/15 bg-[#151218]/70"
                  }`}
                >
                  <p className="text-sm text-slate-300 font-light leading-relaxed line-clamp-3">&ldquo;{r.quote}&rdquo;</p>
                  <div className="flex items-center justify-between gap-4 mt-4">
                    <p className="text-xs text-slate-500">
                      <span className="text-[#F2A7BA]">{r.author}</span>
                      {r.context ? ` · ${r.context}` : ""}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`admin-review-edit-${r.testimonial_id}`}
                        onClick={() => startEditReview(r)}
                        className="w-9 h-9 rounded-full border border-[#E890A7]/20 flex items-center justify-center text-slate-400 hover:text-[#F2A7BA] hover:border-[#E890A7]/50 transition-colors"
                        aria-label="Edit review"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        data-testid={`admin-review-delete-${r.testimonial_id}`}
                        onClick={() => removeReview(r)}
                        className="w-9 h-9 rounded-full border border-red-400/20 flex items-center justify-center text-slate-400 hover:text-red-300 hover:border-red-400/50 transition-colors"
                        aria-label="Delete review"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <div data-testid="admin-enquiries-list" className="space-y-4">
            {contacts.length === 0 && (
              <p className="text-slate-500 text-sm">No enquiries yet — they&apos;ll appear here as visitors write in.</p>
            )}
            {contacts.map((c) => (
              <div
                key={c.contact_id}
                data-testid={`admin-enquiry-${c.contact_id}`}
                className={`rounded-2xl border bg-[#151218]/70 p-5 ${
                  c.source === "calculator" ? "border-[#E890A7]/45" : "border-[#E890A7]/15"
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <p className="font-display text-xl">{c.name}</p>
                    {c.source === "calculator" && (
                      <span
                        data-testid={`admin-lead-tag-${c.contact_id}`}
                        className="rounded-full bg-[#E890A7]/20 border border-[#E890A7]/50 text-[#F2A7BA] text-[10px] font-mono tracking-widest uppercase px-3 py-1"
                      >
                        ★ Ready-to-move lead
                      </span>
                    )}
                  </div>
                  <span className="rounded-full border border-[#E890A7]/30 text-[#F2A7BA] text-[10px] font-mono tracking-widest uppercase px-3 py-1">
                    {c.topic}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {c.email}
                  {c.phone ? ` · ${c.phone}` : ""} · {new Date(c.created_at).toLocaleDateString()}
                </p>
                <p className="text-sm text-slate-300 mt-3 font-light">{c.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "subscribers" && (
          <div data-testid="admin-subscribers-list" className="space-y-4">
            {subscribers.length === 0 && (
              <p className="text-slate-500 text-sm">
                No subscribers yet — visitors join from the market report section of the site.
              </p>
            )}
            {subscribers.map((s) => (
              <div
                key={s.subscriber_id}
                data-testid={`admin-subscriber-${s.subscriber_id}`}
                className="rounded-2xl border border-[#E890A7]/15 bg-[#151218]/70 p-5 flex items-center justify-between gap-4"
              >
                <div>
                  <p className="text-slate-200">{s.email}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Joined {new Date(s.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  data-testid={`admin-subscriber-remove-${s.subscriber_id}`}
                  onClick={() => removeSubscriber(s)}
                  className="w-9 h-9 rounded-full border border-red-400/20 flex items-center justify-center text-slate-400 hover:text-red-300 hover:border-red-400/50 transition-colors"
                  aria-label={`Remove ${s.email}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
        {tab === "businesses" && (
          <div data-testid="admin-businesses-panel">
            <p className="text-slate-400 font-light mb-6">Businesses appear on the Community → Local Businesses page. Add, edit or remove them here.</p>
            <form ref={bizFormRef} onSubmit={saveBiz} className="rounded-2xl border border-[#E890A7]/15 bg-[#151218]/70 p-6 mb-8 grid sm:grid-cols-2 gap-4" data-testid="admin-business-form">
              <input data-testid="biz-name" value={bizForm.name} onChange={(e) => setBizForm({ ...bizForm, name: e.target.value })} placeholder="Business name *" className="rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60" />
              <input data-testid="biz-kind" value={bizForm.kind} onChange={(e) => setBizForm({ ...bizForm, kind: e.target.value })} placeholder="Category (e.g. Café & Bakery)" className="rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60" />
              <textarea data-testid="biz-blurb" value={bizForm.blurb} onChange={(e) => setBizForm({ ...bizForm, blurb: e.target.value })} placeholder="Short description" rows={2} className="sm:col-span-2 rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60" />
              <input data-testid="biz-phone" value={bizForm.phone} onChange={(e) => setBizForm({ ...bizForm, phone: e.target.value })} placeholder="Phone (e.g. 705-555-1234)" className="rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60" />
              <input data-testid="biz-img" value={bizForm.img} onChange={(e) => setBizForm({ ...bizForm, img: e.target.value })} placeholder="Image URL (optional)" className="rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60" />
              <input data-testid="biz-url" value={bizForm.url} onChange={(e) => setBizForm({ ...bizForm, url: e.target.value })} placeholder="Website URL (optional)" className="rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60" />
              <input data-testid="biz-url-label" value={bizForm.url_label} onChange={(e) => setBizForm({ ...bizForm, url_label: e.target.value })} placeholder="Link label (e.g. Menu & ordering)" className="rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60" />
              <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" data-testid="biz-logo" checked={bizForm.logo} onChange={(e) => setBizForm({ ...bizForm, logo: e.target.checked })} /> Image is a logo (fit on white)</label>
              <div className="sm:col-span-2 flex gap-3">
                <button type="submit" data-testid="biz-save" className="rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-6 py-2.5">{editingBiz ? "Update business" : "Add business"}</button>
                {editingBiz && <button type="button" onClick={() => { setEditingBiz(null); setBizForm(emptyBiz); }} className="rounded-full border border-[#E890A7]/25 text-slate-300 px-6 py-2.5">Cancel</button>}
              </div>
            </form>
            <div className="space-y-3">
              {businesses.map((b) => (
                <div key={b.business_id} data-testid={`admin-business-row-${b.business_id}`} className="flex items-start justify-between gap-4 rounded-2xl border border-[#E890A7]/15 bg-[#151218]/60 p-4">
                  <div><p className="font-display text-lg">{b.name}</p><p className="text-xs text-[#E890A7]/70">{b.kind}</p><p className="text-sm text-slate-400 mt-1 font-light">{b.blurb}</p></div>
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`admin-business-edit-${b.business_id}`} onClick={() => startEditBiz(b)} className="w-9 h-9 rounded-full border border-[#E890A7]/20 flex items-center justify-center text-slate-300 hover:text-[#F2A7BA] hover:border-[#E890A7]/50"><Pencil size={15} /></button>
                    <button data-testid={`admin-business-delete-${b.business_id}`} onClick={() => removeBiz(b)} className="w-9 h-9 rounded-full border border-red-400/20 flex items-center justify-center text-slate-400 hover:text-red-300 hover:border-red-400/50"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "events" && (
          <div data-testid="admin-events-panel">
            <p className="text-slate-400 font-light mb-6">Events appear on the Community → Community Events page. Add upcoming events to keep locals coming back.</p>
            <form ref={eventFormRef} onSubmit={saveEvent} className="rounded-2xl border border-[#E890A7]/15 bg-[#151218]/70 p-6 mb-8 grid sm:grid-cols-2 gap-4" data-testid="admin-event-form">
              <input data-testid="event-title" value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} placeholder="Event title *" className="rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60" />
              <input data-testid="event-date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} placeholder="Date (e.g. Sat, July 12, 2026 · 2pm)" className="rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60" />
              <input data-testid="event-location" value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} placeholder="Location" className="sm:col-span-2 rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60" />
              <textarea data-testid="event-description" value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} placeholder="Description" rows={3} className="sm:col-span-2 rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60" />
              <input data-testid="event-img" value={eventForm.img} onChange={(e) => setEventForm({ ...eventForm, img: e.target.value })} placeholder="Image URL (optional)" className="rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60" />
              <input data-testid="event-url" value={eventForm.url} onChange={(e) => setEventForm({ ...eventForm, url: e.target.value })} placeholder="Link URL (optional)" className="rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 outline-none focus:border-[#E890A7]/60" />
              <div className="sm:col-span-2 rounded-xl border border-[#E890A7]/15 bg-[#0D0B0E]/40 p-4">
                <p className="text-xs text-slate-400 font-light mb-3">Optional — fill these in to give visitors an <span className="text-[#F2A7BA]">Add to Calendar</span> button.</p>
                <label className="flex items-center gap-2 text-sm text-slate-300 mb-3 cursor-pointer">
                  <input data-testid="event-allday" type="checkbox" checked={eventForm.all_day} onChange={(e) => setEventForm({ ...eventForm, all_day: e.target.checked })} className="accent-[#E890A7]" />
                  All-day event
                </label>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="text-xs text-slate-400">Start
                    <input data-testid="event-start" type={eventForm.all_day ? "date" : "datetime-local"} value={eventForm.start} onChange={(e) => setEventForm({ ...eventForm, start: e.target.value })} className="mt-1 w-full rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-3 py-2.5 text-slate-100 outline-none focus:border-[#E890A7]/60" />
                  </label>
                  <label className="text-xs text-slate-400">End
                    <input data-testid="event-end" type={eventForm.all_day ? "date" : "datetime-local"} value={eventForm.end} onChange={(e) => setEventForm({ ...eventForm, end: e.target.value })} className="mt-1 w-full rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-3 py-2.5 text-slate-100 outline-none focus:border-[#E890A7]/60" />
                  </label>
                </div>
                <input data-testid="event-rrule" value={eventForm.rrule} onChange={(e) => setEventForm({ ...eventForm, rrule: e.target.value })} placeholder="Repeat rule (advanced, optional) — e.g. RRULE:FREQ=WEEKLY;BYDAY=FR" className="mt-4 w-full rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-2.5 text-slate-100 outline-none focus:border-[#E890A7]/60 text-sm" />
              </div>
              <div className="sm:col-span-2 flex gap-3">
                <button type="submit" data-testid="event-save" className="rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-6 py-2.5">{editingEvent ? "Update event" : "Add event"}</button>
                {editingEvent && <button type="button" onClick={() => { setEditingEvent(null); setEventForm(emptyEvent); }} className="rounded-full border border-[#E890A7]/25 text-slate-300 px-6 py-2.5">Cancel</button>}
              </div>
            </form>
            {events.length === 0 && <p className="text-slate-500 font-light italic">No events yet — add one above and it will appear on the site.</p>}
            <div className="space-y-3">
              {events.map((ev) => (
                <div key={ev.event_id} data-testid={`admin-event-row-${ev.event_id}`} className="flex items-start justify-between gap-4 rounded-2xl border border-[#E890A7]/15 bg-[#151218]/60 p-4">
                  <div><p className="font-display text-lg">{ev.title}</p><p className="text-xs text-[#E890A7]/70">{ev.date}{ev.location ? ` · ${ev.location}` : ""}</p><p className="text-sm text-slate-400 mt-1 font-light">{ev.description}</p></div>
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`admin-event-edit-${ev.event_id}`} onClick={() => startEditEvent(ev)} className="w-9 h-9 rounded-full border border-[#E890A7]/20 flex items-center justify-center text-slate-300 hover:text-[#F2A7BA] hover:border-[#E890A7]/50"><Pencil size={15} /></button>
                    <button data-testid={`admin-event-delete-${ev.event_id}`} onClick={() => removeEvent(ev)} className="w-9 h-9 rounded-full border border-red-400/20 flex items-center justify-center text-slate-400 hover:text-red-300 hover:border-red-400/50"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}